'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Copy01Icon,
  Maximize01Icon,
  Minimize01Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import { MetaDefault, MonoSmall } from '../../components/typography';
import { Textarea } from '../../components/textarea';
import { Clipboard } from '../../components/clipboard';
import { IconButton } from '../../components/icon-button';

const MAX_VISIBLE_URL_CHARS = 100;

type EmailMessageBodyProps = {
  content: string;
  links?: string[];
};

export function EmailMessageBody({
  content,
  links = [],
}: EmailMessageBodyProps) {
  const t = useTranslations('designSystem.mfa.emailDisplay');

  return (
    <VStack alignItems="stretch" gap={2}>
      <HStack alignItems="flex-start" gap={1}>
        {/* SECURITY: Never render mailbox content as HTML (no dangerouslySetInnerHTML / iframe). Untrusted email may contain XSS. Always use a readonly text control so the browser treats it as text. */}
        <Textarea
          value={content}
          readOnly
          rows={6}
          fontFamily="mono"
          fontSize="sm"
          resize="vertical"
          w="full"
        />
        <Clipboard.Root value={content}>
          <Clipboard.Trigger asChild>
            <IconButton variant="ghost" size="xs" aria-label={t('copyMessage')}>
              <Clipboard.Indicator copied={<Tick01Icon size={12} />}>
                <Copy01Icon size={12} />
              </Clipboard.Indicator>
            </IconButton>
          </Clipboard.Trigger>
        </Clipboard.Root>
      </HStack>
      {links.length > 0 && (
        <VStack alignItems="stretch" gap={1}>
          <MetaDefault color="fg.muted">{t('links')}</MetaDefault>
          {links.map((url) => (
            <ExpandableLinkRow key={url} url={url} />
          ))}
        </VStack>
      )}
    </VStack>
  );
}

function ExpandableLinkRow({ url }: { url: string }) {
  const t = useTranslations('designSystem.mfa.emailDisplay');
  const [isExpanded, setIsExpanded] = useState(false);
  const isTruncatable = url.length > MAX_VISIBLE_URL_CHARS;
  const displayUrl =
    isExpanded || !isTruncatable
      ? url
      : `${url.slice(0, MAX_VISIBLE_URL_CHARS)}…`;

  return (
    <HStack gap={1} alignItems="flex-start" minW={0}>
      <Box
        flex={1}
        minW={0}
        aria-expanded={isTruncatable ? isExpanded : undefined}
      >
        {/* SECURITY: Show extracted URLs as text only — never as <a href> or HTML. */}
        <MonoSmall color="fg.muted" wordBreak="break-all">
          {displayUrl}
        </MonoSmall>
        {isTruncatable && (
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={isExpanded ? t('minimize') : t('maximize')}
            title={isExpanded ? t('minimize') : t('maximize')}
            onClick={() => setIsExpanded((previous) => !previous)}
          >
            {isExpanded ? (
              <Minimize01Icon size={12} />
            ) : (
              <Maximize01Icon size={12} />
            )}
          </IconButton>
        )}
      </Box>
      <Clipboard.Root value={url}>
        <Clipboard.Trigger asChild>
          <IconButton variant="ghost" size="xs" aria-label={t('copyUrl')}>
            <Clipboard.Indicator copied={<Tick01Icon size={12} />}>
              <Copy01Icon size={12} />
            </Clipboard.Indicator>
          </IconButton>
        </Clipboard.Trigger>
      </Clipboard.Root>
    </HStack>
  );
}

export type { EmailMessageBodyProps };
